import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { track } from '../../lib/analytics';

export default function LanguageSwitcher({ onSelect }) {
  const { t, i18n } = useTranslation();
  const { darkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // i18n.language can carry a region (es-CL); the base code is what we render.
  const activeCode = i18n.resolvedLanguage || i18n.language?.split('-')[0] || 'en';
  const activeLanguage =
    SUPPORTED_LANGUAGES.find((language) => language.code === activeCode) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (code) => {
    const fromLang = activeCode;
    i18n.changeLanguage(code);
    track('language_changed', { from: fromLang, to: code });
    setIsOpen(false);
    onSelect?.();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('language.select')}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[15px] font-medium transition-colors hover:bg-main/10 ${
          darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
        }`}
      >
        <span className="text-base leading-none">{activeLanguage.flag}</span>
        <span>{activeLanguage.label}</span>
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={t('language.select')}
          className={`absolute left-0 lg4:left-auto lg4:right-0 top-full mt-2 min-w-[130px] py-1 rounded-xl border shadow-lg z-[9999999] ${
            darkMode
              ? 'bg-[#15161d] border-white/10'
              : 'bg-white border-gray-200'
          }`}
        >
          {SUPPORTED_LANGUAGES.map((language) => {
            const isActive = language.code === activeLanguage.code;

            return (
              <li key={language.code} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(language.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[15px] transition-colors ${
                    isActive
                      ? 'text-main font-semibold'
                      : darkMode
                        ? 'text-gray-300 hover:text-white hover:bg-white/5'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-base leading-none">{language.flag}</span>
                  <span className="flex-1">{language.label}</span>
                  {isActive && (
                    <svg
                      className="w-4 h-4 text-main"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
